import { Controller } from "@hotwired/stimulus"

/* Dot indicator for the Money Manager feature row, which snap-scrolls
 * horizontally below 768px and is a plain grid above it (where the dots are
 * hidden by CSS and this controller is simply inert). The active dot is derived
 * from scroll position rather than an IntersectionObserver: with cards wider
 * than the snap step, observer callbacks arrive without any entering entry and
 * the indicator sticks on the last card it saw.
 */
export default class extends Controller {
  static targets = ["scroller", "card", "dot"]

  connect() {
    this._onScroll = this._onScroll.bind(this)
    this._onResize = this._onResize.bind(this)
    this.scrollerTarget.addEventListener("scroll", this._onScroll, { passive: true })
    window.addEventListener("resize", this._onResize, { passive: true })
    this._measure()
    this._sync()
  }

  disconnect() {
    this.scrollerTarget.removeEventListener("scroll", this._onScroll)
    window.removeEventListener("resize", this._onResize)
    if (this._settle) clearTimeout(this._settle)
  }

  go(event) {
    const position = this._positions[Number(event.currentTarget.dataset.index)]
    if (position === undefined) return
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"
    this.scrollerTarget.scrollTo({ left: position, behavior })
  }

  /* Syncing straight from the event keeps the dot honest mid-swipe; the
   * trailing timer catches the settle, because scroll-snap can land the row on
   * its final offset after the last scroll event has already fired.
   */
  _onScroll() {
    this._sync()
    if (this._settle) clearTimeout(this._settle)
    this._settle = setTimeout(() => this._sync(), 150)
  }

  _onResize() {
    this._measure()
    this._sync()
  }

  /* Where the row rests once each card has snapped into place: the card's start
   * edge lands on the scroll port's start edge inset by scroll-padding, so the
   * first card sits at 0 rather than at the row's padding.
   */
  _measure() {
    const scroller = this.scrollerTarget
    const padding = parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0
    this._positions = this.cardTargets.map((card) => card.offsetLeft - scroller.offsetLeft - padding)
  }

  _sync() {
    const position = this.scrollerTarget.scrollLeft
    const nearest = this._positions.reduce(
      (best, left, index) =>
        Math.abs(left - position) < Math.abs(this._positions[best] - position) ? index : best,
      0
    )
    this.dotTargets.forEach((dot, index) => dot.classList.toggle("is-active", index === nearest))
  }
}
